import type { FC, ReactNode } from 'react'

import { AnimatePresence, motion } from 'motion/react'
import { cn } from '@/lib/utils'
import { useDatacenterStore } from '@/components/datacenter/store'
import { glass } from '@/components/datacenter/hud/primitives'

import {
    CATEGORY_META,
    CATEGORY_ORDER,
    STATUS_META,
    THERMAL_STOPS
} from '@/constants/inventory'

const Legend: FC = (): ReactNode => {
    const lens = useDatacenterStore(state => state.lens)
    const airflow = useDatacenterStore(state => state.airflow)
    const first = THERMAL_STOPS[0]
    const last = THERMAL_STOPS[THERMAL_STOPS.length - 1]
    const gradient = `linear-gradient(90deg, ${THERMAL_STOPS.map(stop => `${stop.color} ${((stop.temp - first.temp) / (last.temp - first.temp)) * 100}%`).join(', ')})`

    return (
        <motion.div
            className={cn(glass, 'pointer-events-auto absolute bottom-4 right-4 z-20 hidden w-56 px-3 py-2.5 xl:block')}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.7, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            aria-live='polite'
        >
            <AnimatePresence mode='wait' initial={false}>
                <motion.div key={lens} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.25 }}>
                    <p className='text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500'>
                        {lens === 'thermal' ? 'Temperature' : lens === 'health' ? 'Device health' : 'Zones'}
                    </p>

                    {lens === 'thermal' && (
                        <div className='mt-2'>
                            <div className='h-2 rounded-full' style={{ background: gradient }} />
                            <div className='mt-1 flex justify-between font-mono text-[10px] text-slate-400'>
                                <span>{first.temp} °C</span>
                                <span>{last.temp} °C</span>
                            </div>
                        </div>
                    )}

                    {lens === 'health' && (
                        <ul className='mt-2 grid grid-cols-2 gap-x-3 gap-y-1'>
                            {(['online', 'warning', 'critical', 'maintenance', 'offline'] as const).map(status => (
                                <li key={status} className='flex items-center gap-1.5 text-[11px] text-slate-300'>
                                    <span className='size-2 rounded-full' style={{ backgroundColor: STATUS_META[status].color }} />
                                    {STATUS_META[status].label}
                                </li>
                            ))}
                        </ul>
                    )}

                    {lens === 'standard' && (
                        <ul className='mt-2 space-y-1'>
                            {CATEGORY_ORDER.map(category => (
                                <li key={category} className='flex items-center gap-2 text-[11px] text-slate-300'>
                                    <span className='h-1 w-4 rounded-full' style={{ backgroundColor: CATEGORY_META[category].color, boxShadow: `0 0 8px ${CATEGORY_META[category].color}` }} />
                                    {CATEGORY_META[category].zone}
                                </li>
                            ))}
                        </ul>
                    )}
                </motion.div>
            </AnimatePresence>

            {(airflow || lens === 'thermal') && (
                <div className='mt-2 flex gap-3 border-t border-white/10 pt-2 text-[11px] text-slate-400'>
                    <span className='flex items-center gap-1.5'>
                        <span className='size-2 rounded-full bg-sky-400 shadow-[0_0_8px_#38bdf8]' />
                        Cold air
                    </span>
                    <span className='flex items-center gap-1.5'>
                        <span className='size-2 rounded-full bg-orange-400 shadow-[0_0_8px_#fb923c]' />
                        Hot exhaust
                    </span>
                </div>
            )}
        </motion.div>
    )
}

export default Legend
